import type {
  ToolImage,
  ToolState,
  ImageLabel,
  HistoryEntry,
  CnnSuggestion,
  CnnLabel,
} from '../types/LabelingTypes';

/** Returns a new items array with the item at `key` updated via `updater`. */
function updateItem(
  items: ToolImage[],
  key: string,
  updater: (item: ToolImage) => ToolImage
): ToolImage[] {
  return items.map((item) => (item.key === key ? updater({ ...item }) : item));
}

/** Builds a history entry from the current item state (before mutation). */
function buildHistoryEntry(item: ToolImage): HistoryEntry {
  return { key: item.key, label: item.label };
}

/** Applies a label to a single item; returns [newItems, historyEntry]. */
export function applyLabelReducer(
  items: ToolImage[],
  key: string,
  state: ImageLabel
): [ToolImage[], HistoryEntry] {
  const prev = items.find((i) => i.key === key);
  const entry: HistoryEntry = prev
    ? buildHistoryEntry(prev)
    : { key, label: null };
  const newItems = updateItem(items, key, (item) => ({ ...item, label: state }));
  return [newItems, entry];
}

/** Maps a CNN label to a ToolState. */
export function mapCnnLabel(label: CnnLabel): ToolState {
  return label === 'good' ? 'ok' : 'worn';
}

/** Stores a CNN suggestion on an item (no label applied). */
export function storeCnnSuggestion(
  items: ToolImage[],
  key: string,
  suggestion: CnnSuggestion
): ToolImage[] {
  return updateItem(items, key, (item) => ({ ...item, cnn: suggestion }));
}

/** Clears the CNN suggestion from a single item without changing its label. */
export function rejectSuggestionReducer(items: ToolImage[], key: string): ToolImage[] {
  return updateItem(items, key, (item) => ({ ...item, cnn: null }));
}

/** Clears CNN suggestions for a set of keys without changing labels. */
export function rejectAllSuggestionsReducer(items: ToolImage[], keys: Set<string>): ToolImage[] {
  return items.map((item) => (keys.has(item.key) ? { ...item, cnn: null } : item));
}

/** Clears all labels and CNN suggestions from every item; returns fresh items. */
export function resetAnnotationsReducer(items: ToolImage[]): ToolImage[] {
  return items.map((item) => ({ ...item, label: null, cnn: null }));
}

/** Computes persist-ready labels record from items. */
export function buildLabelsRecord(
  items: ToolImage[]
): Record<string, { label: ImageLabel | null; cnn: CnnSuggestion | null }> {
  return Object.fromEntries(
    items.map((item) => [item.key, { label: item.label, cnn: item.cnn }])
  );
}
